// src/components/Header.js — AIR MAKINA / FINAL MOBILE-FIXED HEADER (COLOR LOGOS, RIGHT HAMBURGER, AUTO-CLOSE)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Header.css';
import logo from '../assets/airmakina-logoo.png';
import { Menu, X } from 'lucide-react';

/* ===============================
   SUBTLE MONOCHROME CANVAS — APPLE/TESLA INSPIRED
================================ */
const AirCanvas = ({ scrollY }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    let w, h, raf;

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = 80;
    };
    resize();
    window.addEventListener('resize', resize);

    // Daha az, daha kontrollü flow çizgisi
    const flows = Array.from({ length: 3 }, (_, i) => ({
      y: 26 + i * 16,
      speed: 0.15 + Math.random() * 0.25,
      opacity: 0.03 + Math.random() * 0.04,
      width: Math.random() * 150 + 80,
      x: -200
    }));

    // Minimal partiküller
    const particles = Array.from({ length: 12 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 0.8 + 0.2,
      vx: Math.random() * 0.18 + 0.04,
      vy: (Math.random() - 0.5) * 0.1,
      a: Math.random() * 0.1 + 0.02
    }));

    const animate = () => {
      ctx.clearRect(0, 0, w, h);

      const intensity = Math.min(scrollY / 1500, 0.25);
      ctx.fillStyle = `rgba(252, 252, 253, ${0.4 + intensity * 0.2})`;
      ctx.fillRect(0, 0, w, h);

      // Flow çizgileri — çok ince, gri, orta yoğunlukta
      flows.forEach(f => {
        f.x += f.speed;
        if (f.x > w + 250) f.x = -250;

        const g = ctx.createLinearGradient(f.x, 0, f.x + f.width, 0);
        g.addColorStop(0, 'rgba(0,0,0,0)');
        g.addColorStop(0.5, `rgba(0,0,0,${f.opacity * (1 - intensity)})`);
        g.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.beginPath();
        ctx.moveTo(f.x, f.y);
        ctx.lineTo(f.x + f.width, f.y);
        ctx.strokeStyle = g;
        ctx.lineWidth = 0.6;
        ctx.stroke();
      });

      // Partiküller — neredeyse fark edilmeyen beyaz noktalar
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x > w + 15) p.x = -15;
        if (p.y > h + 8) p.y = -8;
        if (p.y < -8) p.y = h + 8;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,0,0,${p.a * (1 - intensity * 0.7)})`;
        ctx.fill();
      });

      raf = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [scrollY]);

  return <canvas ref={canvasRef} className="air-art-canvas" aria-hidden="true" />;
};

/* ===============================
   HEADER COMPONENT
================================ */
const Header = ({ onTeklifTikla }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [speed, setSpeed] = useState(38);
  const headerRef = useRef(null);

  // Scroll dinleme — smooth header height
  useEffect(() => {
    const onScroll = () => {
      const currentScroll = window.scrollY;
      setScrollY(currentScroll);
      setSpeed(Math.max(22, 58 - currentScroll * 0.04));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Mobilde body scroll engelleme
  useEffect(() => {
    if (menuOpen && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [menuOpen]);

  // Mobil menüyü dışarı tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuOpen && window.innerWidth < 768) {
        const header = headerRef.current;
        if (header && !header.contains(e.target)) {
          setMenuOpen(false);
        }
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  const headerHeight = Math.max(92, 132 - scrollY * 0.12);

  const goHome = useCallback((e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname === '/') {
      window.location.reload();
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);

  const goTeklif = useCallback((e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollToTeklif: true } });
    } else {
      onTeklifTikla?.();
    }
  }, [location.pathname, navigate, onTeklifTikla]);

  const logos = [
    { src: '/logos/lupamat.png', alt: 'Lupamat', text: 'LUPAMAT YETKİLİ SERVİS' },
    { src: '/logos/atlascopco.png', alt: 'Atlas Copco', text: 'ATLAS COPCO Özel Servis' },
    { src: '/logos/ingersollrand.png', alt: 'Ingersoll Rand', text: 'INGERSOLL RAND Özel Servis' },
    { src: '/logos/kaeser.png', alt: 'Kaeser', text: 'KAESER Özel Servis' },
    { src: '/logos/dalgakiran.png', alt: 'Dalgakıran', text: 'DALGAKIRAN Özel Servis' },
    { src: '/logos/ekomak.png', alt: 'Ekomak', text: 'EKOMAK Özel Servis' },
    { src: '/logos/sarmak.png', alt: 'Sarmak', text: 'SARMAK Özel Servis' },
  ];

  // Mobile menu close handler
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  return createPortal(
    <header ref={headerRef} className="header-art" style={{ height: `${headerHeight}px` }}>
      <AirCanvas scrollY={scrollY} />

      <div className="header-main">
        <div className="logo-zone" onClick={goHome} aria-label="Air Makina Ana Sayfa">
          <img src={logo} className="art-logo" alt="Air Makina" />
          <span className="art-title">AIR MAKINA</span>
        </div>

        <nav className={`nav-art ${menuOpen ? 'open' : ''}`}>
          <a href="/" onClick={(e) => { e.preventDefault(); goHome(e); closeMenu(); }} aria-current={location.pathname === '/' ? 'page' : undefined}>
            Ana Sayfa
          </a>
          <Link to="/urunler" onClick={closeMenu} aria-current={location.pathname === '/urunler' ? 'page' : undefined}>
            Ürünler
          </Link>
          <a href="#teklif" onClick={(e) => { e.preventDefault(); goTeklif(e); closeMenu(); }}>
            Teklif Al
          </a>
          <Link to="/hizmetler" onClick={closeMenu} aria-current={location.pathname === '/hizmetler' ? 'page' : undefined}>
            Hizmetler
          </Link>
          <Link to="/iletisim" onClick={closeMenu} aria-current={location.pathname === '/iletisim' ? 'page' : undefined}>
            İletişim
          </Link>
          <Link to="/login" className="art-button" onClick={closeMenu}>
            Giriş Yap
          </Link>
        </nav>

        <button
          className="menu-art-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-expanded={menuOpen}
          aria-label={menuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Kayan yazı */}
      <div className="kayan-yazi-wrapper">
        <div className="kayan-yazi-track" style={{ animationDuration: `${speed}s` }}>
          <div className="kayan-yazi-icerik">
            {logos.map((item, i) => (
              <div className="kayan-item" key={`original-${i}`}>
                <img src={item.src} alt={item.alt} loading="lazy" />
                <span>{item.text}</span>
              </div>
            ))}
            <div className="kayan-item phone">
              <span>📞 0541 332 91 63</span>
            </div>
          </div>
          <div className="kayan-yazi-icerik clone">
            {logos.map((item, i) => (
              <div className="kayan-item" key={`clone-${i}`}>
                <img src={item.src} alt={item.alt} loading="lazy" />
                <span>{item.text}</span>
              </div>
            ))}
            <div className="kayan-item phone">
              <span>📞 0541 332 91 63</span>
            </div>
          </div>
        </div>
      </div>
    </header>,
    document.body
  );
};

export default Header;